/**
 * What is written on a bar rather than in it: barlines, repeats, endings, the signs that
 * jump between bars, rehearsal marks, breaks, and a clef change partway through a part.
 *
 * All but the clef belong to the global timeline, because they belong to the piece rather
 * than to any one player: a repeat that only the second violins take is not a repeat. The
 * clef is the exception -- it is a property of one staff of one part -- and it is here
 * anyway, because from the user's side it is the same gesture: something stated at a bar.
 *
 * The length of a bar is here for the same reason: a pickup is a bar written in the metre
 * of the piece and holding less than it, which is a thing stated at a bar and not a change
 * of metre.
 */

import { measureTicks } from "../document/duration";
import type { MeasureId, PartId } from "../document/ids";
import { measureIndexOf, occupiedTicks, resolveAttributes } from "../document/query";
import type { BarlineStyle, Clef, JumpMarks, RepeatMarks, Score } from "../document/types";
import type { Command } from "../commands/types";

function hasBar(score: Score, id: MeasureId): boolean {
  return score.measures.some((measure) => measure.id === id);
}

/** How a bar is closed off: a double bar before a new section, a final bar at the end. */
export function setBarline(score: Score, id: MeasureId, style: BarlineStyle): Command | string {
  if (!hasBar(score, id)) return "there is no such bar";
  return { kind: "setMeasure", id, props: { barline: style } };
}

const NO_REPEAT: RepeatMarks = { start: false, end: false, endings: [], times: null };

/** The repeat marks a bar carries now, whether or not it states any. */
function repeatOf(score: Score, id: MeasureId): RepeatMarks {
  return score.measures.find((measure) => measure.id === id)?.repeat ?? NO_REPEAT;
}

/** A bar states nothing about repeats when it has no start, no end and no ending. */
function tidy(marks: RepeatMarks): RepeatMarks | null {
  const bare = !marks.start && !marks.end && marks.endings.length === 0;
  return bare ? null : marks;
}

/** Turn the repeat sign at the start of a bar on or off. */
export function setRepeatStart(score: Score, id: MeasureId, on: boolean): Command | string {
  if (!hasBar(score, id)) return "there is no such bar";
  return { kind: "setMeasure", id, props: { repeat: tidy({ ...repeatOf(score, id), start: on }) } };
}

/** Turn the repeat sign at the end of a bar on or off. */
export function setRepeatEnd(score: Score, id: MeasureId, on: boolean): Command | string {
  if (!hasBar(score, id)) return "there is no such bar";
  const marks = repeatOf(score, id);
  // The count is the count of this wall, and goes with it.
  const repeat = tidy({ ...marks, end: on, times: on ? marks.times : null });
  return { kind: "setMeasure", id, props: { repeat } };
}

/**
 * How many times the section ending at a bar is played, or `null` to leave it to the ending
 * numbers and the verses under it: twice where there are neither.
 *
 * Stated on the bar the repeat ends at, since that is the wall being counted. A count below
 * two is refused: once through is no repeat, and the way to say that is to take the sign off.
 */
export function setRepeatTimes(
  score: Score,
  id: MeasureId,
  times: number | null,
): Command | string {
  if (!hasBar(score, id)) return "there is no such bar";
  const marks = repeatOf(score, id);
  if (!marks.end) return "this bar does not end a repeat";
  if (times !== null && (!Number.isInteger(times) || times < 2)) {
    return "a repeat is played at least twice";
  }
  return { kind: "setMeasure", id, props: { repeat: { ...marks, times } } };
}

/**
 * Put a bar into a numbered ending, or take it out of every ending.
 *
 * Endings are a list because a bar can belong to more than one -- a passage played on both
 * the first and second time through is written `1. 2.` over one bracket.
 */
export function setEndings(
  score: Score,
  id: MeasureId,
  endings: readonly number[],
): Command | string {
  if (!hasBar(score, id)) return "there is no such bar";
  const clean = [...new Set(endings.filter((number) => Number.isInteger(number) && number > 0))];
  const marks = { ...repeatOf(score, id), endings: clean.sort((one, other) => one - other) };
  return { kind: "setMeasure", id, props: { repeat: tidy(marks) } };
}

const NO_JUMP: JumpMarks = { segno: false, coda: false, toCoda: false, fine: false, jump: null };

/** The jumps a bar carries now, whether or not it states any. */
function jumpOf(score: Score, id: MeasureId): JumpMarks {
  return score.measures.find((measure) => measure.id === id)?.jump ?? NO_JUMP;
}

/** A bar states nothing about jumping when it carries no sign and no instruction. */
function tidyJump(marks: JumpMarks): JumpMarks | null {
  const bare = !marks.segno && !marks.coda && !marks.toCoda && !marks.fine && marks.jump === null;
  return bare ? null : marks;
}

/** Take a sign off every bar but one, so that the piece has at most one of it. */
function elsewhere(score: Score, id: MeasureId, sign: "segno" | "coda"): Command[] {
  return score.measures
    .filter((measure) => measure.id !== id && measure.jump?.[sign] === true)
    .map((measure) => ({
      kind: "setMeasure" as const,
      id: measure.id,
      props: { jump: tidyJump({ ...(measure.jump ?? NO_JUMP), [sign]: false }) },
    }));
}

/**
 * Write a jump on a bar: a segno, a coda sign, a To Coda, a Fine, or the D.C. or D.S.
 * itself. Pass only what changes; everything else the bar says is kept.
 *
 * A piece has one segno and one coda, because a D.S. goes back to *the* sign. Putting
 * either on a second bar therefore moves it rather than adding a second one nobody would
 * ever reach -- see {@link ../document/repeats}, which takes the first it finds.
 */
export function setJump(score: Score, id: MeasureId, marks: Partial<JumpMarks>): Command | string {
  if (!hasBar(score, id)) return "there is no such bar";
  const props = { jump: tidyJump({ ...jumpOf(score, id), ...marks }) };
  const moved = [
    ...(marks.segno === true ? elsewhere(score, id, "segno") : []),
    ...(marks.coda === true ? elsewhere(score, id, "coda") : []),
  ];
  const here: Command = { kind: "setMeasure", id, props };
  return moved.length === 0 ? here : { kind: "batch", label: "Jump", commands: [...moved, here] };
}

/** Write a rehearsal mark over a bar, or take it away with `null`. */
export function setRehearsal(score: Score, id: MeasureId, text: string | null): Command | string {
  if (!hasBar(score, id)) return "there is no such bar";
  const trimmed = text === null ? null : text.trim();
  return {
    kind: "setMeasure",
    id,
    props: { rehearsal: trimmed === null || trimmed === "" ? null : trimmed },
  };
}

/** The most any one voice of a bar holds, measured as if the bar were `capacity` long. */
function heaviest(score: Score, id: MeasureId, capacity: number): number {
  let most = 0;
  for (const part of score.parts) {
    const measure = part.measures[id];
    if (measure === undefined) continue;
    for (const voice of measure.voices) {
      // A whole-measure rest is as long as the bar is, whatever that comes to, so it never
      // stands in the way of shortening one.
      const held = voice.events.reduce((total, event) => total + occupiedTicks(event, capacity), 0);
      most = Math.max(most, held);
    }
  }
  return most;
}

/**
 * How long a bar is, when that is not what its metre says; `null` gives it back to the metre.
 *
 * This is the pickup, and the short bar that closes a repeat begun mid-bar. It is a length
 * and not a second time signature on purpose: an upbeat is written in the metre of the
 * piece, and a reader that saw a metre change would print one at the bar.
 */
export function setLength(score: Score, id: MeasureId, ticks: number | null): Command | string {
  const index = measureIndexOf(score, id);
  if (index < 0) return "there is no such bar";
  if (ticks === null) return { kind: "setMeasure", id, props: { length: null } };

  const { time } = resolveAttributes(score, index);
  const metre = measureTicks(time.beats, time.beatType);
  if (ticks <= 0) return "a bar has to be long enough to hold something";
  if (ticks >= metre) return "a bar says its own length only in order to be shorter than its metre";

  const held = heaviest(score, id, ticks);
  if (held > ticks) return "that bar already holds more music than would fit";
  return { kind: "setMeasure", id, props: { length: ticks } };
}

/** Force a new system or page before a bar, or let the engraver decide again with `null`. */
export function setBreak(
  score: Score,
  id: MeasureId,
  before: "system" | "page" | null,
): Command | string {
  if (!hasBar(score, id)) return "there is no such bar";
  return { kind: "setMeasure", id, props: { breakBefore: before } };
}

/**
 * Change the clef of one staff of one part from a bar onwards, or take the change away.
 *
 * The clef a part starts on is part of the staff; this is the change that happens in the
 * middle of a piece, which the bar it happens in carries.
 */
export function setClefAt(
  score: Score,
  partId: PartId,
  measureId: MeasureId,
  staff: number,
  clef: Clef | null,
): Command | string {
  const part = score.parts.find((candidate) => candidate.id === partId);
  if (part === undefined) return "there is no such part";
  if (part.measures[measureId] === undefined) return "that part has nothing for that bar";
  if (part.staves[staff] === undefined) return "that part has no such staff";
  return { kind: "setClef", partId, measureId, staff, clef };
}
